
import { EnrolledTraining } from "../../../../domain/entities/entities";
import { useStore } from "vuex";
import { computed, onBeforeMount } from "@vue/runtime-core";
import { wrapLoading } from "../../utils/wrap_loading";
import { dispatchError } from "../../utils/error_handling";
import { EnrolledTrainingTypes } from "../../../../domain/enums/course";
import LineOnlineTraining from "./LineOnlineTraining.vue";
import LineFaceTraining from "./LineFaceTraining.vue";
import { useRoute, useRouter } from "vue-router";
import { ComputedRef, watch } from "vue";
import { getOrderFromURL } from "../../utils/order_utils";
import Search from "./inputs/Search.vue";
import { search } from "./data-table/table-order-utils";
import { removeQuery, updateQuery } from "../../utils/routing_utils";

export default {
  methods: { search },
  components: { Search, LineFaceTraining, LineOnlineTraining },
  setup() {
    const store = useStore();
    const route = useRoute();
    const router = useRouter();

    const trainings: ComputedRef<EnrolledTraining[]> = computed(() => {
      const enrolledTraining =
          store.getters["userProfile_module/enrolledTraining"] || [];

      const nameQuery = route.query.search?.toString().toLowerCase();

      switch (route.query.filter) {
        case "all":
          return enrolledTraining.filter((training: EnrolledTraining) => {
            if (!nameQuery) return true;
            return training.name?.toLowerCase().includes(nameQuery);
          });
        case "online-finished":
          const onlines = enrolledTraining.filter(
              (training: EnrolledTraining) => {
                return training.type === EnrolledTrainingTypes.ONLINE;
              }
          );
          return onlines
              .filter((training: EnrolledTraining) => {
                return training.progress === 100;
              })
              .filter((training: EnrolledTraining) => {
                if (!nameQuery) return true;
                return training.name?.toLowerCase().includes(nameQuery);
              });
        case "online-in-progress":
          const progress = enrolledTraining.filter(
              (training: EnrolledTraining) => {
                return training.type === EnrolledTrainingTypes.ONLINE;
              }
          );
          return progress
              .filter((training: EnrolledTraining) => {
                return training.progress !== 100;
              })
              .filter((training: EnrolledTraining) => {
                if (!nameQuery) return true;
                return training.name?.toLowerCase().includes(nameQuery);
              });
        case "face":
          return enrolledTraining
              .filter((training: EnrolledTraining) => {
                return training.type === EnrolledTrainingTypes.FACE;
              })
              .filter((training: EnrolledTraining) => {
                if (!nameQuery) return true;
                return training.name?.toLowerCase().includes(nameQuery);
              });
        default:
          return enrolledTraining.filter((training: EnrolledTraining) => {
            if (!nameQuery) return true;
            return training.name?.toLowerCase().includes(nameQuery);
          });
      }
    });

    onBeforeMount(async () => {
      await wrapLoading(async () => {
        await loadCoursesInProgress();
      });
    });

    /** Loads all the courses in which the user has an enrollment (including the finished ones). */
    const loadCoursesInProgress = async (): Promise<void> => {
      const order = getOrderFromURL(route);

      try {
        await store.dispatch("userProfile_module/listEnrolledTraining", order);
      } catch (e) {
        await dispatchError(e);
      }
    };

    watch(
        () => route.query,
        async () => {
          await wrapLoading(async () => {
            await loadCoursesInProgress();
          })
        },
        { deep: true }
    );

    /** Checks if the course is active or not. */
    const isInactive = (training: EnrolledTraining): boolean => {
      const currentDate = new Date().toISOString().split("T")[0];

      if (!training.isActive) {
        return true;
      }

      return (
        currentDate < training.activationDate ||
        currentDate > training.inactivationDate
      );
    };

    const redirectToTraining = async (training: EnrolledTraining) => {
      if (isInactive(training)) return;

      switch (training.type) {
        case EnrolledTrainingTypes.ONLINE:
          await router.push({
            name: "course",
            params: { course_id: training.id }
          });
          break;
        case EnrolledTrainingTypes.FACE:
          await router.push({
            name: "training-course",
            params: {
              trainingCourseID: training.id
            }
          });
          break;
      }
    };

    /** Function responsible for loading the courses in progress. */
    const sortTrainingTypes = async (event: Event): Promise<void> => {
      const input = event.target as HTMLSelectElement;

      if (input.value) {
        await updateQuery(router, "filter", input.value.toString());
      } else {
        await removeQuery(router, "filter");
      }
    };

    return {
      route,
      trainings,
      search,
      EnrolledTrainingTypes,
      sortTrainingTypes,
      isInactive,
      redirectToTraining
    };
  }
};
